import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Button,
  Input,
  Textarea,
  Field,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { FluentTelefonInput } from "../components/forms/widgets/TelefonEingabe";

const BRAND = {
  fontFamilyPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontFamilyAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  colors: {
    pageBg: "#361936",
    primaryPurple: "#4F266B",
    accent: "#F8AA00",
    accentHover: "#F8AA00",
    cardBg: "#fbf9fc",
    border: "#4F266B",
    textOnDark: "#B4AFB7",
    muted: "#B4AFB7",
  },
};

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    backgroundColor: BRAND.colors.pageBg,
    backgroundImage:
      "radial-gradient(120% 120% at 85% 15%, #4F266B 0%, #3f1f57 28%, #361936 62%, #361936 100%)",
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  hero: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("28px", "16px", "20px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("56px", "24px", "28px", "24px"),
    },
  },
  title: {
    marginTop: 0,
    marginBottom: "12px",
    color: BRAND.colors.accent,
    fontSize: "clamp(2rem, 8vw, 5rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    fontFamily: BRAND.fontFamilyAccent,
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "8px",
    color: BRAND.colors.textOnDark,
    fontSize: "clamp(0.95rem, 3.2vw, 1.15rem)",
    fontFamily: BRAND.fontFamilyPrimary,
  },
  formWrap: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("8px", "16px", "28px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("16px", "24px", "40px", "24px"),
    },
  },
  formCard: {
    backgroundColor: BRAND.colors.cardBg,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    boxShadow: tokens.shadow16,
    ...shorthands.padding("16px"),
    fontFamily: BRAND.fontFamilyPrimary,
    "@media (min-width: 768px)": {
      ...shorthands.padding("24px"),
    },
  },
  formTitle: {
    marginTop: 0,
    marginBottom: "8px",
    fontSize: "clamp(1.2rem, 4vw, 1.8rem)",
    color: BRAND.colors.pageBg,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
  },
  formHint: {
    marginTop: 0,
    marginBottom: "18px",
    color: BRAND.colors.muted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  fields: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 980px)": {
      gridTemplateColumns: "1fr 1fr",
      alignItems: "start",
    },
  },
  fullWidth: {
    width: "100%",
  },
  notesField: {
    gridColumn: "1 / -1",
  },
  actions: {
    marginTop: "18px",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitButton: {
    width: "100%",
    minHeight: "44px",
    backgroundColor: BRAND.colors.accent,
    color: BRAND.colors.pageBg,
    border: `1px solid ${BRAND.colors.accent}`,
    fontFamily: BRAND.fontFamilyPrimary,
    fontWeight: 700,
    ":hover": {
      backgroundColor: BRAND.colors.accentHover,
      border: `1px solid ${BRAND.colors.accentHover}`,
      color: BRAND.colors.pageBg,
    },
    "@media (min-width: 640px)": {
      width: "auto",
      minWidth: "180px",
    },
  },
  stateCard: {
    maxWidth: "680px",
    margin: "48px auto",
    backgroundColor: BRAND.colors.cardBg,
    color: BRAND.colors.pageBg,
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow8,
    textAlign: "center",
    fontFamily: BRAND.fontFamilyPrimary,
  },
});

const PublicCustomerForm = () => {
  const styles = useStyles();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [birthDate, setBirthDate] = useState("");

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        setValid(res.ok);
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    checkToken();
  }, [token]);

  const handleSubmit = async () => {
    if (!token) return;
    const res = await fetch(apiUrl(`public-form/${token}`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, email, notes, birthDate }),
    });
    if (res.ok) setSubmitOk(true);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <Spinner label="Formular wird geladen…" />
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          Der Link ist ungültig oder abgelaufen.
        </div>
      </div>
    );
  }

  if (submitOk) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          Vielen Dank, Ihre Daten wurden übermittelt.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h1 className={styles.title}>Kontakt</h1>
        <p className={styles.subtitle}>Herzlich Willkommen bei Courté.</p>
      </div>

      <div className={styles.formWrap}>
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Kundenformular</h2>
          <p className={styles.formHint}>
            Bitte ergänzen oder aktualisieren Sie Ihre Kontaktdaten. Die Angaben werden
            an uns übermittelt und nach Prüfung übernommen.
          </p>

          <div className={styles.fields}>
            <Field
              label="Gründungsdatum"
              hint="Für Privatkunden: Geburtsdatum, für Firmen: Gründungsdatum."
            >
              <Input
                className={styles.fullWidth}
                type="date"
                size="large"
                value={birthDate}
                onChange={(_, data) => setBirthDate(data.value)}
              />
            </Field>

            <Field label="Telefon" hint="Unter welcher Nummer erreichen wir Sie am besten?">
              <FluentTelefonInput
                className={styles.fullWidth}
                size="large"
                value={phone}
                placeholder="z.B. +49 221 123456"
                onChange={setPhone}
              />
            </Field>

            <Field label="E-Mail" hint="Für Rückfragen und Versand von Unterlagen.">
              <Input
                className={styles.fullWidth}
                type="email"
                size="large"
                value={email}
                placeholder="name@beispiel.de"
                onChange={(_, data) => setEmail(data.value)}
              />
            </Field>

            <Field
              className={styles.notesField}
              label="Weitere Hinweise"
              hint="Optional: z.B. Änderungswünsche, bevorzugte Kontaktzeiten oder Anmerkungen."
            >
              <Textarea
                className={styles.fullWidth}
                size="large"
                value={notes}
                placeholder="Ihre Nachricht an uns"
                onChange={(_, data) => setNotes(data.value)}
                resize="vertical"
              />
            </Field>
          </div>

          <div className={styles.actions}>
            <Button className={styles.submitButton} appearance="primary" onClick={handleSubmit}>
              Absenden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCustomerForm;